import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Select, Badge } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';
import CollapsibleTags from './CollapsibleTags';

/**
 * Select component that uses aggregation data from filter API response
 * This replaces UniversalElasticsearchSelect for filters that should use aggregations
 *
 * @param {Object} props
 * @param {string} props.filterKey - Key for this filter in aggregations
 * @param {Array} props.options - Array of {value, label, count} from aggregations
 * @param {Array} props.value - Selected values
 * @param {Function} props.onChange - Change handler
 * @param {string} props.label - Label translation key
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.showCount - Show option counts
 * @param {boolean} props.disabled - Disable the select
 * @param {boolean} props.showSelectedTags - Show selected items as tags
 * @param {Array} props.initialLabels - Labels for cold start restoration [{value, label}, ...]
 */

const AggregationSelect = ({
  filterKey,
  options = [],
  value = [],
  onChange,
  label,
  placeholder,
  showCount = false,
  disabled = false,
  showSelectedTags = true,
  initialLabels = null,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [localValue, setLocalValue] = useState([]);
  const [mobileSearchValue, setMobileSearchValue] = useState('');

  /**
   * Build a label map from initialLabels and options for display purposes
   * Options take precedence over initialLabels
   */
  const labelMap = useMemo(() => {
    const map = new Map();
    // Add initialLabels first (fallback)
    if (Array.isArray(initialLabels)) {
      initialLabels.forEach((item) => {
        if (item?.value) {
          map.set(item.value, item.label || item.name || item.value);
        }
      });
    }
    // Add options (takes precedence)
    options.forEach((opt) => {
      if (opt?.value) {
        map.set(opt.value, opt.label);
      }
    });
    return map;
  }, [initialLabels, options]);

  /**
   * Get display label for a value
   */
  const getLabel = useCallback((val) => {
    return labelMap.get(val) || val;
  }, [labelMap]);

  const handleChange = (selectedValues) => {
    const selectedItems = selectedValues
      .map((val) => {
        const opt = options.find((o) => o.value === val);
        return opt || { value: val, label: getLabel(val) };
      })
      .filter(Boolean);

    onChange(selectedValues, selectedItems);
  };

  /**
   * Handles tag removal
   * @param {string} removedValue - Value to remove
   */
  const handleRemoveTag = (removedValue) => {
    const newValue = Array.isArray(value)
      ? value.filter((v) => v !== removedValue)
      : [];

    const selectedItems = newValue
      .map((val) => {
        const opt = options.find((o) => o.value === val);
        return opt || { value: val, label: getLabel(val) };
      })
      .filter(Boolean);

    onChange(newValue, selectedItems);
  };

  const renderOption = (option) => (
    <div className="elasticsearch-option">
      <span className="option-label">{option.label}</span>
      {showCount && option.count && (
        <Badge count={option.count} className="option-count" />
      )}
    </div>
  );

  // Mobile: Sync localValue with value when sheet opens
  useEffect(() => {
    if (sheetOpen) {
      setLocalValue(Array.isArray(value) ? value : []);
      setMobileSearchValue('');
    }
  }, [sheetOpen, value]);

  // Mobile: Toggle selection
  const handleMobileSelect = useCallback((optionValue) => {
    setLocalValue((prev) => {
      if (prev.includes(optionValue)) {
        return prev.filter((v) => v !== optionValue);
      }
      return [...prev, optionValue];
    });
  }, []);

  // Mobile: Apply selection and close
  const handleMobileDone = useCallback(() => {
    const selectedItems = localValue.map((val) => {
      const opt = options.find((o) => o.value === val);
      return opt || { value: val, label: getLabel(val) };
    });
    onChange(localValue, selectedItems);
    setSheetOpen(false);
  }, [localValue, options, getLabel, onChange]);

  // Mobile: Filter options by search
  const filteredOptions = useMemo(() => {
    if (!mobileSearchValue) return options;
    const search = mobileSearchValue.toLowerCase();
    return options.filter((opt) =>
      opt.label?.toLowerCase().includes(search)
    );
  }, [options, mobileSearchValue]);

  // Mobile: Get display text
  const mobileDisplayValue = useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) return '';
    if (value.length === 1) {
      return getLabel(value[0]);
    }
    return t('common|{{count}} sélectionné(s)', { count: value.length });
  }, [value, getLabel, t]);

  const selectedCount = localValue.length;

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full space-y-2">
        <div className="flex items-center justify-between">
          {label && <label className="block text-xs font-medium text-gray-700">{t(label)}</label>}
        </div>

        {/* Mobile trigger */}
        <div
          onClick={() => !disabled && setSheetOpen(true)}
          className={`w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span className={`text-xs flex-1 truncate ${mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'}`}>
            {mobileDisplayValue || placeholder || t('common|Select options')}
          </span>
          <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
        </div>

        {/* Mobile sheet */}
        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={selectedCount > 0 ? `${t(label)} (${selectedCount})` : t(label)}
          showDone={true}
          onDone={handleMobileDone}
          doneLabel={t('common|Terminé')}
          doneDisabled={false}
        >
          {/* Search */}
          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={(e) => setMobileSearchValue(e.target.value)}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {/* Empty state */}
            {filteredOptions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">{t('common|Aucun résultat trouvé')}</div>
              </div>
            )}

            {/* Options */}
            {filteredOptions.length > 0 && (
              <div className="py-1">
                {filteredOptions.map((option) => {
                  const isSelected = localValue.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                      onClick={() => handleMobileSelect(option.value)}
                    >
                      <div
                        className={`w-4 h-4 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                          isSelected ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <Check size={10} color="#fff" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">{option.label}</div>
                        {showCount && option.count && (
                          <div className="text-xs text-gray-500">{option.count} résultats</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // Desktop render
  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        {label && <label className="select-label">{t(label)}</label>}
      </div>

      <Select
        className="elasticsearch-select"
        mode="multiple"
        value={value}
        onChange={handleChange}
        placeholder={placeholder || t('common|Select options')}
        allowClear
        maxTagCount={1}
        maxTagPlaceholder={(omittedValues) =>
          `+${omittedValues.length} ${omittedValues.length > 1 ? 'sélections' : 'sélection'}`
        }
        optionFilterProp="label"
        filterOption={(input, option) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
        disabled={disabled}
        optionRender={(option) => renderOption(option)}
      >
        {options.map((option) => (
          <Select.Option
            key={option.value}
            value={option.value}
            label={option.label}
          >
            {renderOption(option)}
          </Select.Option>
        ))}
      </Select>

      {showSelectedTags && (
        <CollapsibleTags
          value={value}
          getLabel={getLabel}
          onRemove={handleRemoveTag}
        />
      )}
    </div>
  );
};

export default AggregationSelect;
